
import React, { useState, useRef } from 'react';
import { MenuItem as MenuItemType } from '@/types/menu';
import { MenuItemPopup } from './MenuItemPopup';
import MenuImage from '../core/MenuImage';

interface MenuItemCardProps {
  item: MenuItemType;
  restaurantId?: string;
  primaryColor?: string | null;
  isHappyHour?: boolean;
  shouldLoadImage?: boolean;
}

export const MenuItemCard: React.FC<MenuItemCardProps> = ({ 
  item, 
  restaurantId = '',
  primaryColor = '#8B5CF6',
  isHappyHour = false,
  shouldLoadImage = true
}) => {
  const [showPopup, setShowPopup] = useState(false);
  const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(null);
  const hasHappyHourPrice = item.is_happy_hour && isHappyHour && item.happy_hour_price !== null;
  const isOutOfStock = item.out_of_stock;

  const handleClick = () => {
    setShowPopup(true);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now()
    };
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!touchStartRef.current) return;

    const touch = e.changedTouches[0];
    const touchEnd = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now()
    };

    const touchDuration = touchEnd.time - touchStartRef.current.time;
    const touchDistanceX = Math.abs(touchEnd.x - touchStartRef.current.x);
    const touchDistanceY = Math.abs(touchEnd.y - touchStartRef.current.y);
    const totalDistance = Math.sqrt(touchDistanceX ** 2 + touchDistanceY ** 2);

    // Only trigger popup if:
    // - Touch duration is between 100ms and 2000ms (not too quick, not too long)
    // - Touch movement is less than 15px (not a scroll gesture)
    const isIntentionalTap = touchDuration >= 100 && 
                           touchDuration <= 2000 && 
                           totalDistance < 15;

    if (isIntentionalTap) {
      e.preventDefault();
      setShowPopup(true);
    }

    touchStartRef.current = null;
  };

  const handleClosePopup = () => {
    setShowPopup(false);
  };

  return (
    <>
      <div 
        className={`bg-white rounded-lg shadow-md overflow-hidden transition-all duration-300 cursor-pointer hover:shadow-lg ${
          isOutOfStock ? 'opacity-60' : ''
        }`}
        onClick={handleClick}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div className="flex h-32">
          {/* Image du plat à gauche - taille fixe, image pleine */}
          {item.image && shouldLoadImage && (
            <div className="w-32 h-32 flex-shrink-0 bg-gray-200 overflow-hidden">
              <MenuImage
                src={item.image}
                size="card"
                alt={item.name}
                className="w-full h-full"
                objectFit="cover"
                fallbackText="📷"
              />
            </div>
          )}
          {item.image && !shouldLoadImage && (
            <div className="w-32 h-32 flex-shrink-0 bg-gray-200 animate-pulse" />
          )}
          
          {/* Contenu du plat à droite */}
          <div className="flex-1 p-4 flex flex-col justify-between min-w-0">
            {/* Section principale avec titre et prix */}
            <div className="flex justify-between items-start mb-2">
              <div className="flex-1 min-w-0 pr-3">
                <h3 className="font-semibold text-lg leading-tight mb-1 line-clamp-2">{item.name}</h3>
                {item.description && (
                  <p className="text-gray-600 text-sm line-clamp-2 mb-2">{item.description}</p>
                )}
              </div>
              
              {/* Prix à droite */}
              <div className="flex-shrink-0">
                {hasHappyHourPrice ? (
                  <div className="flex flex-col items-end">
                    <span className="line-through text-gray-400 text-sm">{item.price.toFixed(2)}€</span>
                    <span className="font-bold text-lg" style={{ color: primaryColor }}>
                      {item.happy_hour_price?.toFixed(2)}€
                    </span>
                  </div>
                ) : (
                  <span className="font-bold text-lg">{item.price.toFixed(2)}€</span>
                )}
              </div>
            </div>
            
            {/* Section inférieure avec allergènes et badges */}
            <div className="mt-auto">
              {/* Allergènes */}
              {item.allergens && item.allergens.length > 0 && (
                <div className="mb-2">
                  <span className="text-xs text-red-600">⚠️ Allergènes: {item.allergens.join(', ')}</span>
                </div>
              )}
              
              {/* Badges Happy Hour et Épuisé */}
              <div className="flex justify-between items-center">
                <div>
                  {hasHappyHourPrice && (
                    <span className="text-xs text-orange-600 font-semibold bg-orange-100 px-2 py-1 rounded">Happy Hour!</span>
                  )}
                </div>
                {isOutOfStock && (
                  <span className="text-xs text-red-600 font-semibold bg-red-100 px-2 py-1 rounded">Épuisé</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Popup */}
      {showPopup && (
        <MenuItemPopup
          item={item}
          restaurantId={restaurantId}
          primaryColor={primaryColor}
          isHappyHour={isHappyHour}
          onClose={handleClosePopup}
        />
      )}
    </>
  );
};

export default MenuItemCard;
